/**
 * 存储辅助工具类
 * 统一管理localStorage操作，提供错误处理和默认值
 */
import DebugHelper from './DebugHelper.js';

export default class StorageHelper {
    /**
     * 从localStorage获取数据
     * @param {string} key - 存储键
     * @param {*} defaultValue - 默认值（如果获取失败或不存在）
     * @returns {*} 存储的数据或默认值
     */
    static getItem(key, defaultValue = null) {
        if (!key) {
            DebugHelper.warn('StorageHelper: 键名不能为空');
            return defaultValue;
        }
        
        try {
            const data = localStorage.getItem(key);
            if (data === null) {
                return defaultValue;
            }
            
            // 尝试解析JSON
            try {
                return JSON.parse(data);
            } catch (parseError) {
                // 如果不是JSON，直接返回字符串
                return data;
            }
        } catch (error) {
            DebugHelper.error(`StorageHelper: 获取数据失败 [${key}]:`, error);
            return defaultValue;
        }
    }
    
    /**
     * 保存数据到localStorage
     * @param {string} key - 存储键
     * @param {*} value - 要存储的值
     * @returns {boolean} 是否保存成功
     */
    static setItem(key, value) {
        if (!key) {
            DebugHelper.warn('StorageHelper: 键名不能为空');
            return false;
        }
        
        try {
            // 如果是对象或数组，转换为JSON
            const dataToStore = typeof value === 'string' ? value : JSON.stringify(value);
            localStorage.setItem(key, dataToStore);
            return true;
        } catch (error) {
            DebugHelper.error(`StorageHelper: 保存数据失败 [${key}]:`, error);
            
            // 如果是存储空间不足错误，提供友好提示
            if (error.name === 'QuotaExceededError') {
                DebugHelper.error('StorageHelper: 存储空间不足，请清理浏览器缓存');
            }
            
            return false;
        }
    }
    
    /**
     * 从localStorage删除数据
     * @param {string} key - 存储键
     * @returns {boolean} 是否删除成功
     */
    static removeItem(key) {
        if (!key) {
            DebugHelper.warn('StorageHelper: 键名不能为空');
            return false;
        }
        
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            DebugHelper.error(`StorageHelper: 删除数据失败 [${key}]:`, error);
            return false;
        }
    }
    
    /**
     * 检查localStorage是否可用
     * @returns {boolean} 是否可用
     */
    static isAvailable() {
        try {
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (error) {
            return false;
        }
    }
    
    /**
     * 清空所有localStorage数据
     * @returns {boolean} 是否清空成功
     */
    static clear() {
        try {
            localStorage.clear();
            return true;
        } catch (error) {
            DebugHelper.error('StorageHelper: 清空数据失败:', error);
            return false;
        }
    }
    
    /**
     * 获取所有存储的键
     * @returns {Array<string>} 键数组
     */
    static getAllKeys() {
        try {
            return Object.keys(localStorage);
        } catch (error) {
            DebugHelper.error('StorageHelper: 获取所有键失败:', error);
            return [];
        }
    }
}

